/**
 * MoveNode Component
 *
 * Custom React Flow node for displaying moves in the series canvas.
 *
 * Features:
 * - Displays move thumbnail, name, and description
 * - Source and target handles for connections
 * - Clickable to navigate to move detail page
 * - Hover effects and visual feedback
 * - Responsive design
 */

import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { cn } from '@/lib/utils'

// Data structure passed to each node.
// The [key: string] index signature satisfies @xyflow/react v12's
// `Node<NodeData extends Record<string, unknown>>` constraint.
export interface MoveNodeData {
  moveId: string             // id of the underlying Move entity
  name: string
  description?: string
  thumbnailUrl?: string | null
  url?: string | null        // video URL
  tagName?: string | null    // the move's first tag, for the Tribe canvas node's chip
  onClick?: (event?: React.MouseEvent) => void
  [key: string]: unknown
}

// Props interface for the component
interface MoveNodeProps {
  data: MoveNodeData
}

/**
 * MoveNode - Custom node component for React Flow
 *
 * This component represents a single move in the series flow diagram.
 * It includes connection handles on the left (target) and right (source)
 * for creating directional connections between moves.
 *
 * @param data - Node data containing move information and click handler
 */
export const MoveNode = memo(function MoveNode({ data }: MoveNodeProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (data.onClick) {
      data.onClick(e)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && data.onClick) {
      e.preventDefault()
      data.onClick()
    }
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        'rounded-lg border-2 border-[#0033A0] bg-white p-4',
        'w-[280px] shadow-md transition-all duration-200',
        'hover:border-[#6B3FA0] hover:shadow-xl',
        data.onClick && 'cursor-pointer',
        'relative'
      )}
      style={{ touchAction: 'none' }}
      role="button"
      tabIndex={0}
      aria-label={`Move: ${data.name}. Click to view details.`}
      onKeyDown={handleKeyDown}
    >
      {/* Target Handle - Incoming connections (left side) */}
      <Handle
        type="target"
        position={Position.Left}
        className="!h-5 !w-5 !border-2 !border-white !bg-[#0033A0] transition-colors hover:!bg-[#6B3FA0]"
        style={{ left: -10 }}
        aria-label="Connection target"
      />

      {/* Thumbnail */}
      {data.thumbnailUrl ? (
        <div className="mb-4 h-32 w-full overflow-hidden rounded bg-gray-100">
          <img
            src={data.thumbnailUrl}
            alt={data.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="mb-4 flex h-32 w-full items-center justify-center rounded bg-gradient-to-br from-[#0033A0]/10 to-[#6B3FA0]/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-[#0033A0]/40"
            aria-hidden="true"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <line x1="9" y1="15" x2="15" y2="15" />
          </svg>
        </div>
      )}

      {/* Move Name */}
      <h3
        className="mb-2 line-clamp-2 text-base font-semibold leading-tight text-black"
        title={data.name}
      >
        {data.name}
      </h3>

      {/* Move Description */}
      {data.description && (
        <p
          className="line-clamp-2 text-sm leading-snug text-gray-600"
          title={data.description}
        >
          {data.description}
        </p>
      )}

      {/* Click hint (shows on hover) */}
      {data.onClick && (
        <div className="pointer-events-none absolute inset-0 rounded-lg bg-[#0033A0]/0 transition-colors hover:bg-[#0033A0]/5" />
      )}

      {/* Source Handle - Outgoing connections (right side) */}
      <Handle
        type="source"
        position={Position.Right}
        className="!h-5 !w-5 !border-2 !border-white !bg-[#0033A0] transition-colors hover:!bg-[#6B3FA0]"
        style={{ right: -10 }}
        aria-label="Connection source"
      />
    </div>
  )
})

// Display name for debugging
MoveNode.displayName = 'MoveNode'
